// src/lib/recaptchaUtils.js

/**
 * Detects the browser environment. In-app browsers / WebViews (Instagram, Facebook,
 * TikTok, WhatsApp, Xiaomi Scanner, Google app, etc.) frequently break reCAPTCHA because of
 * restricted cookies/storage and third-party iframe limits.
 */
export function getBrowserEnv() {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return { name: 'unknown', isInApp: false, isAndroid: false, isIOS: false, isMiui: false };
    }
    const ua = navigator.userAgent || navigator.vendor || '';

    const isIOS = /iPhone|iPad|iPod/i.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/i.test(ua);

    const socialInApp = /FBAN|FBAV|FB_IAB|Instagram|TikTok|musical_ly|BytedanceWebview|Line\/|Snapchat|MicroMessenger|WhatsApp|Twitter|LinkedInApp|Pinterest|Telegram/i.test(ua);
    const androidWebView = isAndroid && /; wv\)/i.test(ua);
    // iOS WKWebView inside apps: has AppleWebKit + Mobile but no "Safari/" token
    const iosWebView = isIOS && /AppleWebKit/i.test(ua) && !/Safari\//i.test(ua);
    const googleApp = /GSA\//i.test(ua);
    const isMiui = /MiuiBrowser|XiaoMi/i.test(ua);

    let name = 'other';
    if (/SamsungBrowser/i.test(ua)) name = 'samsung';
    else if (isMiui) name = 'miui';
    else if (/CriOS/i.test(ua)) name = 'chrome-ios';
    else if (/FxiOS|Firefox/i.test(ua)) name = 'firefox';
    else if (/EdgA|EdgiOS|Edg\//i.test(ua)) name = 'edge';
    else if (/OPR|Opera/i.test(ua)) name = 'opera';
    else if (/YaBrowser/i.test(ua)) name = 'yandex';
    else if (/HeyTapBrowser|HuaweiBrowser|VivoBrowser|UCBrowser/i.test(ua)) name = 'vendor';
    else if (/Chrome/i.test(ua)) name = 'chrome';
    else if (/Safari/i.test(ua)) name = 'safari';

    const isInApp = socialInApp || androidWebView || iosWebView || googleApp;
    if (isInApp) name = 'in-app';

    return { name, isInApp, isAndroid, isIOS, isMiui };
}

/** Kept for backwards compatibility. */
export function isInAppBrowser() {
    return getBrowserEnv().isInApp;
}

/**
 * Android: an intent URL that opens the current page in Chrome (falls back to the normal URL).
 * Returns null on other platforms.
 */
export function getOpenInChromeUrl() {
    if (typeof window === 'undefined') return null;
    const env = getBrowserEnv();
    if (!env.isAndroid) return null;
    const { host, pathname, search, hash, href } = window.location;
    return `intent://${host}${pathname}${search}${hash}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(href)};end`;
}

/**
 * Translates Firebase Phone Auth error codes to user-friendly Turkish messages.
 * @param {Error} err
 * @returns {string}
 */
export function getPhoneAuthErrorMessage(err) {
    if (!err) return 'Bilinmeyen bir hata oluştu.';

    const code = err.code || '';
    const message = err.message || '';

    switch (code) {
        case 'auth/captcha-check-failed':
        case 'auth/invalid-app-credential':
        case 'auth/missing-app-credential':
            if (/hostname/i.test(message)) {
                return 'Bu adres üzerinden güvenlik doğrulaması yapılamıyor. Lütfen siteyi doğrudan kodutara.web.app adresinden açıp tekrar deneyin.';
            }
            return 'Güvenlik doğrulaması bu tarayıcıda tamamlanamadı. Lütfen tekrar deneyin; sorun sürerse sayfayı Chrome veya Safari ile açın.';
        case 'app/recaptcha-timeout':
            return 'Güvenlik doğrulaması zaman aşımına uğradı. Lütfen tekrar deneyin ve açılan resim doğrulamasını tamamlayın.';
        case 'auth/invalid-phone-number':
        case 'auth/missing-phone-number':
            return 'Geçersiz telefon numarası. Lütfen geçerli bir cep telefonu numarası giriniz (örn: 5XX XXX XX XX).';
        case 'auth/too-many-requests':
            return 'Çok fazla doğrulama denemesi yapıldı. Güvenlik nedeniyle lütfen bir süre bekleyip tekrar deneyiniz.';
        case 'auth/quota-exceeded':
            return 'Günlük SMS doğrulama kotasına ulaşıldı. Lütfen daha sonra tekrar deneyiniz.';
        case 'auth/network-request-failed':
            return 'İnternet bağlantısı hatası. Lütfen bağlantınızı kontrol edip tekrar deneyiniz.';
        case 'auth/invalid-verification-code':
            return 'Girdiğiniz 6 haneli doğrulama kodu hatalı. Lütfen SMS ile gelen kodu kontrol ediniz.';
        case 'auth/code-expired':
            return 'Doğrulama kodunun süresi doldu. Lütfen yeni bir kod isteyiniz.';
        case 'auth/operation-not-allowed':
            return 'Bu bölgeye SMS doğrulaması şu anda kapalı. Lütfen yöneticiyle iletişime geçin.';
        case 'auth/internal-error':
            return 'Güvenlik bileşeni yüklenemedi. Lütfen sayfayı yenileyip tekrar deneyin.';
        default:
            return `SMS gönderilemedi: ${message || 'Lütfen bilgilerinizi kontrol edip tekrar deneyin.'}`;
    }
}

/** True when the error is a reCAPTCHA / browser-environment problem (show the browser tip). */
export function isRecaptchaEnvironmentError(err) {
    const code = err?.code || '';
    return [
        'auth/captcha-check-failed',
        'auth/invalid-app-credential',
        'auth/missing-app-credential',
        'auth/internal-error',
        'app/recaptcha-timeout',
    ].includes(code);
}
